"use client";

import { useEffect, useMemo, useRef, useState, type RefObject } from "react";

import { ReportDay } from "@/components/reports/ReportDay";
import { ReportLogo } from "@/components/reports/ReportLogo";
import { ReportMonth } from "@/components/reports/ReportMonth";
import { REPORT_CLASSES, REPORT_THEME } from "@/components/reports/reportTheme";

import { buildDemoReports, DEMO_COMPANY, DEMO_PATIENT } from "./demoReport";

// The client report portal in a window, with sample data. The content is drawn by the same
// components the portal uses, so what the visitor sees is what an agency's client receives.
// The report scrolls by itself while the section is on screen: down to the end, a short pause,
// and back to the top. The first touch of the reader stops it for good on this visit, so the
// reader's own scroll is never fought with.

/**
 * Tuning of the automatic scroll. Change these to adjust the feel:
 * - AUTO_SCROLL_PX_PER_SECOND: speed of the scroll, in pixels per second. Lower is calmer.
 * - AUTO_SCROLL_START_MARGIN_PX: how far below the bottom edge of the screen the section may
 *   be when the scroll starts. 0 starts as the section touches the edge; 200 starts a bit
 *   before it shows.
 * - AUTO_SCROLL_PAUSE_MS: wait at the end of the scroll, before going back to the top.
 */
const AUTO_SCROLL_PX_PER_SECOND = 30;
const AUTO_SCROLL_START_MARGIN_PX = 200;
const AUTO_SCROLL_PAUSE_MS = 1500;

type Mode = "day" | "month";

const MODES: { mode: Mode; label: string }[] = [
  { mode: "day", label: "1 día" },
  { mode: "month", label: "Últimos 30 días" },
];

export function ReportWindow({
  trackRef,
  label,
  note,
}: {
  /** The section whose visibility on the screen starts and pauses the automatic scroll. */
  trackRef: RefObject<HTMLElement | null>;
  label: string;
  note: string;
}) {
  const [mode, setMode] = useState<Mode>("month");
  const reports = useMemo(() => buildDemoReports(), []);
  const scrollerRef = useRef<HTMLDivElement>(null);
  // Kept across mode changes: once the reader has touched the report, it does not move by itself again.
  const stoppedByReaderRef = useRef(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const scroller = scrollerRef.current;
    const section = trackRef.current;
    if (reduceMotion || !scroller || !section) return;

    let position = 0;
    let direction: 1 | -1 = 1;
    let frame = 0;
    let timer = 0;
    let last = 0;
    let finished = false;
    let visible = false;

    const maxScroll = () => scroller.scrollHeight - scroller.clientHeight;

    const cancel = () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      frame = 0;
      timer = 0;
    };

    const tick = (now: number) => {
      const seconds = last ? (now - last) / 1000 : 0;
      last = now;
      position += direction * AUTO_SCROLL_PX_PER_SECOND * seconds;
      position = Math.min(maxScroll(), Math.max(0, position));
      scroller.scrollTop = position;

      const atEdge = direction === 1 ? position >= maxScroll() : position <= 0;
      if (!atEdge) {
        frame = window.requestAnimationFrame(tick);
        return;
      }
      frame = 0;
      if (direction === 1) {
        direction = -1;
        resume(AUTO_SCROLL_PAUSE_MS);
      } else {
        finished = true;
      }
    };

    const resume = (delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = 0;
        last = 0;
        frame = window.requestAnimationFrame(tick);
      }, delay);
    };

    const pause = () => {
      cancel();
    };

    // Scrolling the report, dragging its bar or pressing a key inside it: the reader has taken over.
    const stopForReader = () => {
      stoppedByReaderRef.current = true;
      cancel();
    };

    const onVisibility = ([entry]: IntersectionObserverEntry[]) => {
      visible = entry.isIntersecting;
      if (!visible) {
        pause();
        return;
      }
      if (stoppedByReaderRef.current || finished || frame || timer) return;
      resume(0);
    };

    // The root is grown at the bottom by the start margin, so the section counts as visible
    // just before it reaches the screen edge.
    const observer = new IntersectionObserver(onVisibility, {
      rootMargin: `0px 0px ${AUTO_SCROLL_START_MARGIN_PX}px 0px`,
      threshold: 0,
    });
    observer.observe(section);

    scroller.addEventListener("wheel", stopForReader, { passive: true });
    scroller.addEventListener("touchstart", stopForReader, { passive: true });
    scroller.addEventListener("pointerdown", stopForReader);
    scroller.addEventListener("keydown", stopForReader);

    return () => {
      observer.disconnect();
      cancel();
      scroller.removeEventListener("wheel", stopForReader);
      scroller.removeEventListener("touchstart", stopForReader);
      scroller.removeEventListener("pointerdown", stopForReader);
      scroller.removeEventListener("keydown", stopForReader);
    };
  }, [trackRef, mode]);

  return (
    <div
      role="group"
      aria-label={label}
      style={REPORT_THEME}
      className="flex h-[520px] min-w-0 flex-col overflow-hidden rounded-xl bg-[var(--rp-bg)] text-left text-[var(--rp-fg)] shadow-2xl shadow-black/40 ring-1 ring-white/10"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[var(--rp-line)] bg-[var(--rp-surface)] px-4 py-3">
        <ReportLogo name={DEMO_COMPANY} url={null} />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-base font-semibold leading-tight">
            {DEMO_PATIENT}
          </span>
          <span className="truncate text-xs text-[var(--rp-muted)]">
            {DEMO_COMPANY}
          </span>
        </div>
        <div
          className="ml-auto flex gap-1"
          role="tablist"
          aria-label="Tipo de reporte"
        >
          {MODES.map((item) => (
            <button
              key={item.mode}
              type="button"
              role="tab"
              aria-selected={mode === item.mode}
              className={`${REPORT_CLASSES.tab} ${mode === item.mode ? REPORT_CLASSES.tabOn : REPORT_CLASSES.tabOff}`}
              onClick={() => setMode(item.mode)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* scrollBehavior auto: the website sets smooth on every element, and each automatic
          step would restart that animation, so the report would never reach its position. */}
      <div
        ref={scrollerRef}
        tabIndex={0}
        style={{ scrollBehavior: "auto" }}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--rp-accent)]"
      >
        {mode === "day" ? (
          <ReportDay report={reports.day} />
        ) : (
          <ReportMonth report={reports.month} />
        )}
        <p className={`${REPORT_CLASSES.note} pt-4`}>{note}</p>
      </div>
    </div>
  );
}
